<template>
  <div class="home-section">
    <!-- 标题区域 -->
    <template v-if="categoryList.length > 0">
      <div v-if="categoryList.length > 1" class="section-title-wrapper">
        <span v-for="(item, idx) in categoryList" :key="idx" class="title-item">
          <a 
            v-if="item.templatePath" 
            :href="item.templatePath + '?categoryId=' + item.id" 
            target="_blank"
          >
            {{ item.name }}
          </a>
          <span v-else>{{ item.name }}</span>
          <span v-if="idx < categoryList.length - 1 && item.templatePath" class="title-separator">·</span>
        </span>
      </div>
      <div v-else class="section-title">{{ categoryList[0].name }}</div>
    </template>
    <template v-else>
      <template v-if="categoryMap && Object.keys(categoryMap).length > 0">
        <div class="section-title-wrapper" v-if="filteredCategoryLinks.length > 0">
          <span 
            v-for="(link, idx) in filteredCategoryLinks" 
            :key="idx" 
            class="title-item"
          >
            <a 
              :href="link.href" 
              target="_blank"
              @click.prevent="openLink(link.href)"
            >
              {{ splitTitles[idx] }}
            </a>
            <span v-if="idx < filteredCategoryLinks.length - 1" class="title-separator">·</span>
          </span>
        </div>
        <div v-else class="section-title">{{ title }}</div>
      </template>
      <div v-else class="section-title">{{ title }}</div>
    </template>
    
    <div class="section-body" :class="sectionBodyClass">
      <!-- 文章区域 -->
      <div v-if="!hideArticle && article" class="article-area">
        <a class="article-title" @click="goDetail(article.newsCode)">{{ article.title }}</a>
        <p class="article-summary">
          <span 
            class="summary-text" 
            :title="article.summary"
            @click="goDetail(article.newsCode)"
          >{{ article.summary }}</span>
        </p>
        <div class="article-meta">
          <span class="category-name">{{ article.categoryName }}</span>
          <div class="article-meta-right">
            <span class="article-source">{{ getSource(article.source) }}</span>
            <span class="article-time-divider">|</span>
            <span class="article-time">{{ getFormattedTime(article.publishTime) }}</span>
            <div class="article-actions">
              <ShareButton :news-code="article.newsCode" :title="article.title" />
              <FavoriteButton :news-id="article.id" />
            </div>
          </div>
        </div>
      </div>
      <!-- 列表区域 -->
      <div class="list-area" :style="listStyle">
        <a v-for="(item, idx) in list" :key="idx" class="list-item" @click="goDetail(item.newsCode)">
          {{ item.title }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchHomeNewsList } from '../../../api'
import request from '../../../api/request'
import FavoriteButton from '../../channel/components/FavoriteButton.vue'
import ShareButton from '../../channel/components/ShareButton.vue'

export default {
  name: 'HomeSection',
  components: { FavoriteButton, ShareButton },
  props: {
    title: {
      type: String,
      default: ''
    },
    reverse: {
      type: Boolean,
      default: false
    },
    hideArticle: {
      type: Boolean,
      default: false
    },
    categoryIdList: {
      type: Array,
      default: () => []
    },
    categoryMap: {
      type: Object,
      default: () => ({})
    },
    positionKey: {
      type: String,
      default: ''
    },
    // 新增：列表区域位置，可选 'left' 或 'right'，默认 'left'
    listPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value)
    }
  },
  data() {
    return {
      article: null,
      list: [],
      categoryList: [],
      newsList: []
    }
  },
  computed: {
    listStyle() {
      if (this.hideArticle) {
        return { width: '100%' }
      }
      return { width: '320px' }
    },
    // 标题分割文字 (保持原标题结构)
    splitTitles() {
      return this.title.split('·').map(item => item.trim())
    },
    // 过滤有链接的分类
    filteredCategoryLinks() {
      return this.splitTitles
        .map(title => {
          const category = this.categoryMap[title]
          if (category && category.templatePath) {
            return {
              title,
              id: category.id,
              href: category.templatePath + '?categoryId=' + category.id
            }
          }
          return null
        })
        .filter(item => item !== null)
    },
    // 新增：section-body 的动态 class
    sectionBodyClass() {
      return {
        reverse: this.reverse,
        'article-hidden': this.hideArticle,
        'list-on-left': this.listPosition === 'left' && !this.hideArticle,
        'list-on-right': this.listPosition === 'right' && !this.hideArticle
      }
    }
  },
  watch: {
    categoryIdList() {
      this.loadNews()
    }
  },
  created() {
    this.loadNews()
  },
  methods: {
    async loadNews() {
      if (!this.positionKey) {
        // 旧方式：使用 categoryIdList
        if (!this.categoryIdList || this.categoryIdList.length === 0) return
        try {
          const data = await fetchHomeNewsList(this.categoryIdList)
          if (data.length > 0) {
            this.article = data[0]
          }
          this.list = data.slice(1)
        } catch (err) {
          // 接口失败保持空
        }
        return
      }

      // 新方式：使用 positionKey 调用新接口
      try {
        const res = await request.post('/api/index/getIndexRecommendInfo', { positionKey: this.positionKey })
        if (res.code === 200 && res.data) {
          this.categoryList = res.data.categoryList || []
          this.newsList = res.data.newsList || []

          // 如果没有分类列表，仍然使用 old 方式
          if (this.categoryList.length === 0 && this.categoryIdList.length > 0) {
            const data = await fetchHomeNewsList(this.categoryIdList)
            if (data.length > 0) {
              this.article = data[0]
            }
            this.list = data.slice(1)
          } else {
            // 使用新接口返回的数据
            if (this.newsList.length > 0) {
              this.article = this.newsList[0]
            }
            this.list = this.newsList.slice(1)
          }
        }
      } catch (err) {
        console.error('加载新闻失败:', err)
      }
    },
    openLink(href) {
      if (href) {
        window.open(href, '_blank')
      }
    },
    getSource(source) {
      if (!source) return ''
      const truncated = source.split('|')[0]
      return this.truncateSevenChars(truncated)
    },
    truncateSevenChars(str) {
      if (!str) return ''
      if (str.length <= 7) return str
      return str.slice(0, 7) + '...'
    },
    // 格式化时间为：xx 月 xx 日 时：分:秒
    getFormattedTime(timeStr) {
      if (!timeStr) return ''
      try {
        const date = new Date(timeStr)
        if (isNaN(date.getTime())) return timeStr
        
        const month = date.getMonth() + 1
        const day = date.getDate()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')
        
        return `${month}月${day}日 ${hours}:${minutes}:${seconds}`
      } catch (err) {
        console.error('时间格式化失败:', err)
        return timeStr
      }
    },
    goDetail(newsCode) {
      if (!newsCode) return
      window.open(`/zp/a/${newsCode}`, '_blank')
    }
  }
}
</script>

<style scoped>
.home-section {
  width: 1400px;
  margin: 0 auto;
  box-sizing: border-box;
  margin-top: 10px;
}
.section-title {
  width: 261px;
  height: 45px;
  color: rgba(185, 24, 22, 1);
  font-size: 34px;
  font-family: 'Microsoft YaHei', sans-serif;
  text-align: left;
  white-space: nowrap;
  line-height: 34px;
  margin-left: 1px;
}

/* 标题容器 */
.section-title-wrapper {
  display: inline-block;
  margin-left: 1px;
}

/* 标题项 */
.section-title-wrapper .title-item {
  display: inline;
}

/* 链接文本 - inline 保证在一行 */
.section-title-wrapper a {
  display: inline;
  color: #B91816;
  font-size: 34px;
  font-family: 'Microsoft YaHei', sans-serif;
  line-height: 34px;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.section-title-wrapper a:hover {
  color: #C00513;
  text-decoration: underline;
}

/* 分隔符：独立 span，不是超链接 */
.section-title-wrapper .title-separator {
  display: inline;
  color: #B91816;
  font-size: 34px;
  font-family: 'Microsoft YaHei', sans-serif;
  line-height: 34px;
  margin: 0 8px;       /* 左右间距一致 */
  cursor: default;     /* 默认光标，不显示手型 */
  user-select: none;   /* 不可选中 */
}
.section-body {
  width: 1395px;
  height: 318px;
  display: flex;
  gap: 35px;
  margin-top: 15px;
}
.section-body.reverse {
  flex-direction: row-reverse;
  justify-content: flex-start;
}
.section-body.reverse .list-area {
  margin-left: 0;
}
.section-body.article-hidden {
  height: auto;
}

/* 新增：列表在左侧（默认） */
.section-body.list-on-left {
  /* 默认 flex-direction: row; 不需要额外设置 */
}

/* 新增：列表在右侧 */
.section-body.list-on-right {
  flex-direction: row-reverse;
}

.article-area {
  width: 1040px;
  height: 318px;
  background-color: rgba(245, 245, 247, 1);
  box-sizing: border-box;
  padding: 30px 0 0 30px;
  display: grid;
  flex-direction: column;
  /* justify-content: flex-end; */
}
.article-title {
  width: 966px;
  height: 40px;
  color: rgba(0, 0, 0, 1);
  font-size: 30px;
  font-family: 'Microsoft YaHei', sans-serif;
  text-align: left;
  white-space: nowrap;
  line-height: 40px;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  /* margin-left: 24px; */
}
.article-title:hover {
  color: rgba(185, 24, 22, 1);
}
.article-summary {
  width: 966px;
  color: #666;
  font-size: 16px;
  font-family: 'Microsoft YaHei', sans-serif;
  text-align: left;
  line-height: 32px;
  margin: -15px 0 0 5px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* 限制最多三行 */
  -webkit-box-orient: vertical;
  cursor: pointer;
  height: 100px;
}

.article-summary:hover {
  color: rgba(185, 24, 22, 1);
}
.article-meta {
  width: 962px;
  height: auto;
  display: flex;
  align-items: center;
  margin: 21px 0 0 0;
  font-size: 14px;
  font-family: 'Microsoft YaHei UI', sans-serif;
}
.category-name {
  padding: 5px 10px;
  background-color: rgba(238, 238, 238, 1);
  color: rgba(51, 51, 51, 1);
  font-size: 14px;
  white-space: nowrap;
  line-height: 14px;
  margin-right: auto;
}
.article-meta-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.article-source {
  max-width: 118px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(51, 51, 51, 1);
  font-size: 14px;
  line-height: 14px;
}
.article-time-divider {
  color: rgba(102, 102, 102, 1);
  font-size: 14px;
  white-space: nowrap;
  line-height: 14px;
  margin: 0 8px;
}
.article-time {
  color: rgba(51, 51, 51, 1);
  font-size: 14px;
  white-space: nowrap;
  line-height: 14px;
}
.article-actions {
  display: flex;
  align-items: center;
  gap: 25px;
      margin-right: -15px;
    margin-left: 25px;
}
.action-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(102, 102, 102, 1);
  cursor: pointer;
  font-size: 14px;
  line-height: 14px;
  user-select: none;
}
.action-item img {
  width: 14px;
  height: 14px;
  margin-top: 0;
}
.action-item:hover {
  opacity: 0.8;
}
.list-area {
  width: 320px;
  height: 318px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 4px 0;
}
.list-item {
  width: 320px;
  height: 52px;
  color: rgba(0, 0, 0, 1);
  font-size: 18px;
  font-family: 'Microsoft YaHei', sans-serif;
  text-align: left;
  line-height: 52px;
  text-decoration: none;
  cursor: pointer;
  border-bottom: 1px dashed rgba(230, 230, 230, 1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.list-item:last-child {
  border-bottom: none;
}
.list-item:hover {
  color: rgba(185, 24, 22, 1);
}
</style>